<template>
	<EventLayout :event="event" :subtitle="trans('events.enrollments.resolve')">
		<template #actions>
			<PrimaryButton v-if="allowResolution" :color="Color.Green" @click.stop="showModal('resolve-enrollments-modal')">
				<i class="fa-solid fa-check mr-2"/>
				{{ trans('general.continue') }}
			</PrimaryButton>
		</template>
		<Modal id="resolve-enrollments-modal">
			<div class="px-3 py-2 flex items-end gap-4">
				<LabeledObject>
					<template #label>{{ trans('general.types.groups.file') }}</template>
					<input id="file-input" type="file" @change="loadFile">
				</LabeledObject>
				<LabeledObject>
					<template #label>{{ trans('enrollments.honored') }}</template>
					<NumberInput v-model.number="honored"/>
				</LabeledObject>
				<div class="grow"/>
				<PrimaryButton :color="Color.Green" @click.stop="resolve">
					<i class="fa-solid fa-check"/>
				</PrimaryButton>
			</div>
		</Modal>
		<HStack class="grid-cols-3">
			<VStack>
				<Heading>{{ trans('enrollments.status.attached.title') }}</Heading>
				<SubHeading>{{ transChoice('enrollments.count', attachedEnrollments.length) }}</SubHeading>
				<ResolveEnrollment v-for="enrollment in attachedEnrollments" :key="`attached-enrollment-${enrollment.id}`" :enrollment="enrollment" :allow-switching="!allowResolution" @accept="upgrade(enrollment.id)" @reject="downgrade(enrollment.id)"/>
			</VStack>
			<VStack>
				<Heading>{{ trans('enrollments.status.accepted.title') }}</Heading>
				<SubHeading>{{ transChoice('enrollments.count', acceptedEnrollments.length) }}</SubHeading>
				<ResolveEnrollment v-for="enrollment in acceptedEnrollments" :key="`accepted-enrollment-${enrollment.id}`" :enrollment="enrollment" :allow-switching="!allowResolution" @accept="upgrade(enrollment.id)" @reject="downgrade(enrollment.id)"/>
			</VStack>
			<VStack>
				<Heading>{{ trans('enrollments.status.rejected.title') }}</Heading>
				<SubHeading>{{ transChoice('enrollments.count', rejectedEnrollments.length) }}</SubHeading>
				<ResolveEnrollment v-for="enrollment in rejectedEnrollments" :key="`rejected-enrollment-${enrollment.id}`" :enrollment="enrollment" :allow-switching="!allowResolution" @accept="upgrade(enrollment.id)" @reject="downgrade(enrollment.id)"/>
			</VStack>
		</HStack>
	</EventLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {Color} from '@/Types/Enums/Color'
import {defineProps, type Ref, ref} from 'vue'
import {type Event} from '@/Types/Events/Event'
import Modal from '@/Components/UI/Modals/Modal.vue'
import EventLayout from '@/Layouts/EventLayout.vue'
import {trans, transChoice} from 'laravel-vue-i18n'
import HStack from '@/Components/UI/Stacks/HStack.vue'
import VStack from '@/Components/UI/Stacks/VStack.vue'
import readXlsxFile, {type Row} from 'read-excel-file'
import Heading from '@/Components/UI/Typography/Heading.vue'
import {type Enrollment} from '@/Types/Events/Enrollment'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import NumberInput from '@/Components/UI/Inputs/NumberInput.vue'
import SubHeading from '@/Components/UI/Typography/SubHeading.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import ResolveEnrollment from '@/Components/Events/Enrollments/ResolveEnrollment.vue'
import {formatNumber, showError, showInfo, showModal, showSuccess, showValidationErrors, showWarning} from '@/Helpers/general'

const props = defineProps<{
	event:Event,
	allowResolution:boolean,
	attachedEnrollments:Array<Enrollment>,
	acceptedEnrollments:Array<Enrollment>,
	rejectedEnrollments:Array<Enrollment>,
}>()

const honored:Ref<number> = ref(0)
const event:Ref<Event> = ref(props.event)
const working:Ref<boolean> = ref(false)
const allowResolution:Ref<boolean> = ref(props.allowResolution)
const attachedEnrollments:Ref<Array<Enrollment>> = ref(props.attachedEnrollments)
const acceptedEnrollments:Ref<Array<Enrollment>> = ref(props.acceptedEnrollments)
const rejectedEnrollments:Ref<Array<Enrollment>> = ref(props.rejectedEnrollments)

function resolve():void{
	if(attachedEnrollments.value.length > 0){
		showWarning(trans('enrollments.status.resolution.verification.no_attached'))
	} else if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('events.enrollments.resolve.update', event.value.id), {
			honored: honored.value,
			acceptedEnrollments: acceptedEnrollments.value,
			rejectedEnrollments: rejectedEnrollments.value,
		}).then(() => {
			showSuccess(trans('general.updated'))
			allowResolution.value = false
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function loadFile():void{
	let fileInput = document.getElementById('file-input') as HTMLInputElement
	let resolutionFile = null
	if(fileInput && fileInput.files){
		if(fileInput.files.length === 1){
			resolutionFile = fileInput.files[0]
			if(resolutionFile){
				readXlsxFile(resolutionFile).then((rows:Array<Row>) => {
					validateFile(rows)
					rows.forEach((cells) => {
						let enrollmentId:number = Number(cells[0])
						let score:number = Number(cells[1])
						let status:string = String(cells[2])
						const enrollmentIndex:number = getAttachedEnrollmentIndexById(enrollmentId)
						if(status === 'accepted'){
							acceptAttached(enrollmentIndex, score)
						}
						if(status === 'rejected'){
							rejectAttached(enrollmentIndex, score)
						}
					})
				}).catch(showError)
			}
			fileInput.value = ''
		}
	}
}

function validateFile(rows:Array<Row>):void{
	for(let rowIndex = 0, status, cells; rowIndex < rows.length; rowIndex++){
		cells = rows[rowIndex]
		status = String(cells[2]).trim()
		if(!verifyAttachedEnrollmentId(Number(cells[0]))){
			throw trans('enrollments.status.resolution.verification.enrollment_id', {
				row: (rowIndex + 1).toString(),
				value: String(cells[0]),
			})
		}
		if(!verifyScore(Number(cells[1]))){
			throw trans('enrollments.status.resolution.verification.score', {
				row: (rowIndex + 1).toString(),
				value: String(cells[1]),
			})
		}
		if(!verifyStatus(status)){
			throw trans('enrollments.status.resolution.verification.status', {
				row: (rowIndex + 1).toString(),
				value: status,
			})
		}
	}
}

function verifyAttachedEnrollmentId(value:number):boolean{
	return attachedEnrollments.value.map((e:Enrollment) => e.id).includes(value)
}

function verifyScore(value:number):boolean{
	return value >= 0 && value <= 100
}

function verifyStatus(value:string):boolean{
	return ['accepted', 'rejected'].includes(value)
}

function acceptAttached(enrollmentIndex:number, score:number|null = null):void{
	attachedEnrollments.value[enrollmentIndex].is_attached = false
	attachedEnrollments.value[enrollmentIndex].is_rejected = false
	attachedEnrollments.value[enrollmentIndex].is_accepted = true
	attachedEnrollments.value[enrollmentIndex].status = 'accepted'
	if(score){
		attachedEnrollments.value[enrollmentIndex].score = parseFloat(formatNumber(score, 2, '.', ''))
	}
	acceptedEnrollments.value.push(attachedEnrollments.value.splice(enrollmentIndex, 1)[0])
	acceptedEnrollments.value.sort((a:Enrollment, b:Enrollment) => b.score - a.score)
}

function acceptRejected(enrollmentIndex:number, score:number|null = null):void{
	rejectedEnrollments.value[enrollmentIndex].is_attached = false
	rejectedEnrollments.value[enrollmentIndex].is_rejected = false
	rejectedEnrollments.value[enrollmentIndex].is_accepted = true
	rejectedEnrollments.value[enrollmentIndex].status = 'accepted'
	if(score){
		rejectedEnrollments.value[enrollmentIndex].score = parseFloat(formatNumber(score, 2, '.', ''))
	}
	acceptedEnrollments.value.push(rejectedEnrollments.value.splice(enrollmentIndex, 1)[0])
	acceptedEnrollments.value.sort((a:Enrollment, b:Enrollment) => b.score - a.score)
}

function upgrade(enrollmentId:number, score:number|null = null):void{
	const enrollmentIndex = getRejectedEnrollmentIndexById(enrollmentId)
	if(!working.value && confirm(trans('enrollments.status.resolution.confirm_upgrade', {
		user: rejectedEnrollments.value[enrollmentIndex].user.full_name
	}))){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('events.enrollments.resolve.accept', enrollmentId)).then(() => {
			showSuccess(trans('general.updated'))
			acceptRejected(enrollmentIndex, score)
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function rejectAttached(enrollmentIndex:number, score:number|null = null):void{
	attachedEnrollments.value[enrollmentIndex].is_attached = false
	attachedEnrollments.value[enrollmentIndex].is_accepted = false
	attachedEnrollments.value[enrollmentIndex].is_rejected = true
	attachedEnrollments.value[enrollmentIndex].status = 'rejected'
	if(score){
		attachedEnrollments.value[enrollmentIndex].score = parseFloat(formatNumber(score, 2, '.', ''))
	}
	rejectedEnrollments.value.push(attachedEnrollments.value.splice(enrollmentIndex, 1)[0])
	rejectedEnrollments.value.sort((a:Enrollment, b:Enrollment) => b.score - a.score)
}

function rejectAccepted(enrollmentIndex:number, score:number|null = null):void{
	acceptedEnrollments.value[enrollmentIndex].is_attached = false
	acceptedEnrollments.value[enrollmentIndex].is_accepted = false
	acceptedEnrollments.value[enrollmentIndex].is_rejected = true
	acceptedEnrollments.value[enrollmentIndex].status = 'rejected'
	if(score){
		acceptedEnrollments.value[enrollmentIndex].score = parseFloat(formatNumber(score, 2, '.', ''))
	}
	rejectedEnrollments.value.push(acceptedEnrollments.value.splice(enrollmentIndex, 1)[0])
	rejectedEnrollments.value.sort((a:Enrollment, b:Enrollment) => b.score - a.score)
}

function downgrade(enrollmentId:number, score:number|null = null):void{
	const enrollmentIndex = getAcceptedEnrollmentIndexById(enrollmentId)
	if(!working.value && confirm(trans('enrollments.status.resolution.confirm_downgrade', {
		user: acceptedEnrollments.value[enrollmentIndex].user.full_name
	}))){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('events.enrollments.resolve.reject', enrollmentId)).then(() => {
			showSuccess(trans('general.updated'))
			rejectAccepted(enrollmentIndex, score)
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function getAttachedEnrollmentIndexById(enrollmentId:number):number{
	return attachedEnrollments.value.findIndex((e:Enrollment) => e.id === enrollmentId)
}

function getAcceptedEnrollmentIndexById(enrollmentId:number):number{
	return acceptedEnrollments.value.findIndex((e:Enrollment) => e.id === enrollmentId)
}

function getRejectedEnrollmentIndexById(enrollmentId:number):number{
	return rejectedEnrollments.value.findIndex((e:Enrollment) => e.id === enrollmentId)
}
</script>
